"use client";

import { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { usePathname, useRouter } from "@/lib/cwfNavigation";
import { flagMenuTransitionFromDuel } from "@/lib/coinFlyToHud";
import { playUiHaptic } from "@/lib/haptics";
import {
  BlueGradientBezel,
  CreamPanel,
  CreamSectionHeader,
} from "@/components/profile/panelPrimitives";
import { useAudioSettings } from "@/stores/audioSettings";

/**
 * Gradient for the faux track layer (sibling div, not `::-webkit-slider-*`).
 *
 * Decorative stops must stay in ascending order: the original design used fixed
 * stops at 18% / 42% / 72% before the volume cut. When volume is below 72%,
 * those stops sit *past* the cut; CSS gradient stop-fixup then shoves the warm
 * colors outward so the fill no longer matches the thumb.
 *
 * We scale 18/42/72 by `(volume/100)` so at full volume the look matches the
 * old art, and at lower volumes every stop stays ≤ the cut line.
 */
function volumeTrackFillBackground(pct: string): string {
  const raw = Number.parseFloat(pct.replace("%", ""));
  const v = Number.isFinite(raw) ? Math.max(0, Math.min(100, raw)) : 0;
  const s18 = (v * 18) / 100;
  const s42 = (v * 42) / 100;
  const s72 = (v * 72) / 100;
  return `linear-gradient(to right, #fef9c3 0%, #fbbf24 ${s18}%, #eab308 ${s42}%, #d97706 ${s72}%, #b45309 ${v}%, #b9a984 ${v}%, #b9a984 100%)`;
}

/** Native track is transparent; yellow fill is a sibling div with inline `background`. */
const VOLUME_SLIDER_CLASS =
  "absolute inset-0 h-10 w-full cursor-pointer appearance-none bg-transparent outline-none disabled:cursor-not-allowed " +
  "[&::-webkit-slider-runnable-track]:h-[14px] [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-transparent " +
  "[&::-webkit-slider-thumb]:-mt-[9px] [&::-webkit-slider-thumb]:h-7 [&::-webkit-slider-thumb]:w-7 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-[3px] [&::-webkit-slider-thumb]:border-[#0a355c] [&::-webkit-slider-thumb]:bg-gradient-to-b [&::-webkit-slider-thumb]:from-[#e8f7ff] [&::-webkit-slider-thumb]:to-[#258BD4] [&::-webkit-slider-thumb]:shadow-[0_3px_8px_rgba(0,0,0,0.35)] " +
  "[&::-moz-range-track]:h-[14px] [&::-moz-range-track]:rounded-full [&::-moz-range-track]:border-0 [&::-moz-range-track]:bg-transparent " +
  "[&::-moz-range-progress]:h-[14px] [&::-moz-range-progress]:rounded-l-full [&::-moz-range-progress]:bg-transparent " +
  "[&::-moz-range-thumb]:h-7 [&::-moz-range-thumb]:w-7 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-[3px] [&::-moz-range-thumb]:border-[#0a355c] [&::-moz-range-thumb]:bg-gradient-to-b [&::-moz-range-thumb]:from-[#e8f7ff] [&::-moz-range-thumb]:to-[#258BD4] [&::-moz-range-thumb]:shadow-[0_3px_8px_rgba(0,0,0,0.35)]";

type VolumeSliderProps = {
  pct: string;
  value: number;
  disabled: boolean;
  onVolumeChange: (unit: number) => void;
  ariaLabel: string;
};

function VolumeSlider({
  pct,
  value,
  disabled,
  onVolumeChange,
  ariaLabel,
}: VolumeSliderProps) {
  return (
    <div
      className={
        disabled ? "relative h-10 w-full opacity-40" : "relative h-10 w-full"
      }
    >
      <div
        aria-hidden
        className="pointer-events-none absolute left-0 right-0 top-1/2 h-[14px] -translate-y-1/2 rounded-full shadow-[inset_0_2px_5px_rgba(40,30,12,0.28)]"
        style={{ background: volumeTrackFillBackground(pct) }}
      />
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={value}
        disabled={disabled}
        onChange={(e) => onVolumeChange(Number(e.target.value) / 100)}
        className={`${VOLUME_SLIDER_CLASS} accent-[#258BD4]`}
        aria-label={ariaLabel}
      />
    </div>
  );
}

type Props = {
  open: boolean;
  onClose: () => void;
};

export function AudioSettingsModal({ open, onClose }: Props) {
  const pathname = usePathname();
  const router = useRouter();
  const musicVolume = useAudioSettings((s) => s.musicVolume);
  const sfxVolume = useAudioSettings((s) => s.sfxVolume);
  const musicMuted = useAudioSettings((s) => s.musicMuted);
  const sfxMuted = useAudioSettings((s) => s.sfxMuted);
  const setMusicVolume = useAudioSettings((s) => s.setMusicVolume);
  const setSfxVolume = useAudioSettings((s) => s.setSfxVolume);
  const setMusicMuted = useAudioSettings((s) => s.setMusicMuted);
  const setSfxMuted = useAudioSettings((s) => s.setSfxMuted);

  const showQuitMatch = pathname === "/play";

  const closeSilent = useCallback(() => {
    onClose();
  }, [onClose]);

  const closeWithTap = useCallback(() => {
    playUiHaptic("light");
    onClose();
  }, [onClose]);

  const quitMatch = useCallback(() => {
    playUiHaptic("medium");
    flagMenuTransitionFromDuel();
    onClose();
    router.replace("/");
  }, [onClose, router]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeSilent();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeSilent]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  const musicPct = `${Math.round(musicVolume * 100)}%`;
  const sfxPct = `${Math.round(sfxVolume * 100)}%`;

  return createPortal(
    <div
      className="fixed inset-0 z-[500] flex items-center justify-center bg-black/55 p-3 backdrop-blur-[3px] sm:p-4"
      role="presentation"
      onClick={closeSilent}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Audio and settings"
        className="relative z-[1] flex max-h-[min(42rem,92dvh)] w-full max-w-md min-h-0 min-w-0 flex-col overflow-y-auto overflow-x-hidden rounded-none border-0 bg-transparent p-0 shadow-none sm:max-h-[min(40rem,88dvh)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative mx-auto w-full min-w-0 max-w-full px-0.5">
          <BlueGradientBezel className="p-3 sm:p-4">
            <CreamPanel className="flex min-h-0 flex-col overflow-hidden shadow-[inset_0_2px_0_rgba(255,247,221,0.92),0_6px_0_rgba(78,43,33,0.1)]">
              <CreamSectionHeader
                title="Audio & settings"
                className="py-2.5 sm:py-3"
                titleClassName="!text-[0.72rem] sm:!text-[0.8rem] tracking-[0.12em]"
              />
              <div className="space-y-5 bg-[#FFF0C8] px-3 py-4 sm:px-4 sm:py-4">
                <p className="text-center font-display text-[0.68rem] font-semibold uppercase tracking-[0.08em] text-[#7B422F]/85 sm:text-[0.72rem]">
                  Music & sound effects
                </p>

                <section className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-display text-[10px] font-black uppercase tracking-wide text-[#7B422F] sm:text-[11px]">
                      Music
                    </span>
                    <label className="flex cursor-pointer items-center gap-1.5 font-display text-[9px] font-bold uppercase text-[#61331f]/90 sm:text-[10px]">
                      <input
                        type="checkbox"
                        checked={musicMuted}
                        onChange={(e) => {
                          playUiHaptic("light");
                          setMusicMuted(e.target.checked);
                        }}
                        className="h-5 w-5 rounded border-2 border-[#0a355c] bg-[#fff7dd] accent-[#258BD4] sm:h-6 sm:w-6"
                      />
                      Mute
                    </label>
                  </div>
                  <div className="rounded-2xl border-2 border-[#d4b48a]/80 bg-[#FFF7DD] px-3 py-2 shadow-[inset_0_2px_6px_rgba(60,40,20,0.08)]">
                    <VolumeSlider
                      pct={musicPct}
                      value={Math.round(musicVolume * 100)}
                      disabled={musicMuted}
                      onVolumeChange={setMusicVolume}
                      ariaLabel="Music volume"
                    />
                  </div>
                </section>

                <div className="border-t border-[#e2c99a]/90 pt-5">
                  <section className="space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-display text-[10px] font-black uppercase tracking-wide text-[#7B422F] sm:text-[11px]">
                        Sound effects
                      </span>
                      <label className="flex cursor-pointer items-center gap-1.5 font-display text-[9px] font-bold uppercase text-[#61331f]/90 sm:text-[10px]">
                        <input
                          type="checkbox"
                          checked={sfxMuted}
                          onChange={(e) => {
                            playUiHaptic("light");
                            setSfxMuted(e.target.checked);
                          }}
                          className="h-5 w-5 rounded border-2 border-[#0a355c] bg-[#fff7dd] accent-[#258BD4] sm:h-6 sm:w-6"
                        />
                        Mute
                      </label>
                    </div>
                    <div className="rounded-2xl border-2 border-[#d4b48a]/80 bg-[#FFF7DD] px-3 py-2 shadow-[inset_0_2px_6px_rgba(60,40,20,0.08)]">
                      <VolumeSlider
                        pct={sfxPct}
                        value={Math.round(sfxVolume * 100)}
                        disabled={sfxMuted}
                        onVolumeChange={setSfxVolume}
                        ariaLabel="Sound effects volume"
                      />
                    </div>
                  </section>
                </div>

                {showQuitMatch ? (
                  <div className="border-t border-[#e2c99a]/90 pt-5">
                    <button
                      type="button"
                      onClick={quitMatch}
                      className="flex min-h-[2.85rem] w-full touch-manipulation items-center justify-center rounded-2xl border-[3px] border-[#9f2419] bg-gradient-to-b from-[#FF7D65] to-[#D94132] px-3 py-2.5 font-display text-[0.72rem] font-black uppercase tracking-[0.06em] text-white shadow-[0_4px_0_rgba(70,20,12,0.55),inset_0_2px_0_rgba(255,255,255,0.35)] transition hover:brightness-105 active:translate-y-0.5 active:shadow-[0_2px_0_rgba(70,20,12,0.45),inset_0_1px_0_rgba(255,255,255,0.28)] [-webkit-tap-highlight-color:transparent] sm:min-h-[3rem] sm:text-[0.78rem]"
                      aria-label="Quit match and return to main menu"
                    >
                      <span className="drop-shadow-[0_2px_0_rgba(60,20,12,0.55)]">
                        Quit match
                      </span>
                    </button>
                  </div>
                ) : null}
              </div>
            </CreamPanel>
          </BlueGradientBezel>
        </div>
      </div>
      <button
        type="button"
        className="fixed right-3 top-[max(0.65rem,env(safe-area-inset-top))] z-[501] flex h-10 w-10 items-center justify-center rounded-full border-[3px] border-[#9f2419] bg-gradient-to-b from-[#FF7D65] to-[#D94132] font-display text-base font-black leading-none text-white shadow-[0_4px_0_rgba(70,20,12,0.55),inset_0_2px_0_rgba(255,255,255,0.35)] transition hover:brightness-105 active:translate-y-0.5 sm:right-4 sm:top-[max(1rem,env(safe-area-inset-top))] sm:text-lg lg:right-6 lg:top-6"
        aria-label="Close audio settings"
        onClick={closeWithTap}
      >
        <span className="drop-shadow-[0_2px_0_rgba(60,20,12,0.55)]">✕</span>
      </button>
    </div>,
    document.body,
  );
}
